import React, { useEffect } from "react"
import type { FC } from "react"
import { Outlet } from "react-router"

import { ConnectivityErrorScreen } from "@/components/connectivity-error/connectivity-error-screen"
import { ErrorBoundaryContentFallback } from "@/components/error-boundary/error-boundary-content-fallback"
import { Page404 } from "@/components/nav/page404"
import {
  ActiveOrganizationProvider,
  UserSettingsProvider,
  useAuthentication,
  useAuthorization,
} from "@/contexts"
import { useGtagSetUser } from "@/services/gtag"
import { usePendoInit } from "@/services/pendo"
import { isConnectivityError } from "@/utils/api"
import { datadogRum } from "@datadog/browser-rum"

import { useRetryConnectivityRecovery } from "../use-retry-connectivity-recovery"

export const AuthenticatedRoute: FC = () => {
  const { activeUser, authenticatedUser, isAuthenticated } = useAuthentication()
  const {
    authorizedOrganizations,
    authorizedOrganizationsError,
    authorizedOrganizationsIsError,
    authorizedOrganizationsIsFetched,
  } = useAuthorization()
  const { isRetrying, retry } = useRetryConnectivityRecovery()
  // Set the gtag user_id here if user loads a page and is already authenticated
  useGtagSetUser()
  usePendoInit()

  // Start session replay once authenticated
  useEffect(() => {
    if (isAuthenticated) {
      datadogRum.startSessionReplayRecording()
    }
  }, [isAuthenticated])

  if (!isAuthenticated) {
    return null
  }

  if (isConnectivityError(authorizedOrganizationsError)) {
    return <ConnectivityErrorScreen isRetrying={isRetrying} onRetry={retry} />
  }

  if (authorizedOrganizationsIsError) {
    return <ErrorBoundaryContentFallback fullScreen />
  }

  if (
    authorizedOrganizationsIsFetched &&
    authorizedOrganizations.length === 0
  ) {
    return <Page404 />
  }

  return (
    activeUser &&
    authenticatedUser &&
    authorizedOrganizationsIsFetched &&
    authorizedOrganizations.length > 0 && (
      <ActiveOrganizationProvider>
        <UserSettingsProvider>
          <Outlet />
        </UserSettingsProvider>
      </ActiveOrganizationProvider>
    )
  )
}
